import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface LoginPromptContextValue {
  promptLogin: (action?: string) => void;
}

const LoginPromptContext = createContext<LoginPromptContextValue>({
  promptLogin: () => {},
});

export function LoginPromptProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<string | undefined>();

  const promptLogin = useCallback((nextAction?: string) => {
    setAction(nextAction);
    setOpen(true);
  }, []);

  const close = () => setOpen(false);

  return (
    <LoginPromptContext.Provider value={{ promptLogin }}>
      {children}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Sign in to participate</h2>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
              {action ? `You need to be signed in to ${action}.` : 'You need to be signed in to participate.'}
              {' '}Join the discussion by logging in or creating an account.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link
                to="/login"
                className="btn-primary flex-1 text-center"
                onClick={close}
              >
                Log In
              </Link>
              <Link
                to="/register"
                className="btn-secondary flex-1 text-center"
                onClick={close}
              >
                Create Account
              </Link>
            </div>
            <button
              type="button"
              onClick={close}
              className="mt-4 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}
    </LoginPromptContext.Provider>
  );
}

export function useLoginPrompt() {
  return useContext(LoginPromptContext);
}
